import React, { useState } from 'react';
import { CheckCircle, Users, TrendingUp, Target, Mail, Calendar, ChevronRight, Phone, MapPin, User } from 'lucide-react';

const AdvertisingLanding = () => {
  const [selectedIndustry, setSelectedIndustry] = useState('retail');

  const industryStats = {
    retail: {
      engagement: "32%",
      reach: "15,000+",
      conversion: "4.8x"
    },
    restaurant: {
      engagement: "41%",
      reach: "22,000+",
      conversion: "5.2x"
    },
    services: {
      engagement: "28%",
      reach: "18,000+",
      conversion: "3.9x"
    }
  };

  const testimonials = [
    {
      quote: "We saw a 47% increase in new customers within the first month. The ability to target specific neighborhoods in E-town made all the difference.",
      author: "Sarah Mitchell",
      business: "The Coffee Station, Elizabethtown",
      metric: "47% customer growth",
      image: "/api/placeholder/80/80"
    },
    {
      quote: "The targeted campaign helped us reach exactly the right customers in our area. The results exceeded our expectations.",
      author: "Mike Johnson",
      business: "Johnson's Hardware, Radcliff",
      metric: "156 new customers in first month",
      image: "/api/placeholder/80/80"
    },
    {
      quote: "The ROI tracking is incredible. We can see exactly how many Vine Grove residents engage with our ads and visit our store.",
      author: "Lisa Thompson",
      business: "Thompson's Family Pharmacy, Vine Grove",
      metric: "$12.4k revenue attributed",
      image: "/api/placeholder/80/80"
    }
  ];

  const successStories = [
    {
      business: "Local Restaurant",
      challenge: "New location opening in Elizabethtown",
      solution: "Targeted grand opening campaign",
      result: "Full capacity in first week",
      roi: "386% ROI"
    },
    {
      business: "Service Provider",
      challenge: "Expanding to Radcliff area",
      solution: "Zip code targeted display ads",
      result: "42 new clients in 30 days",
      roi: "295% ROI"
    },
    {
      business: "Retail Store",
      challenge: "Slow summer season",
      solution: "Multi-channel promotion",
      result: "67% revenue increase",
      roi: "412% ROI"
    }
  ];

  const upcomingEvents = [
    "Fort Knox Military Appreciation Week",
    "Elizabethtown Summer Festival",
    "Radcliff Business Expo",
    "Vine Grove Community Fair"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with announcement bar */}
      <div className="bg-blue-900 text-white text-center py-2 text-sm">
        Limited Time: Get 25% off your first 3 months of advertising. Ends soon!
      </div>
      
      <header className="bg-white shadow">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <nav className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Hardin Local</h1>
            <div className="flex items-center gap-6">
              <a href="#case-studies" className="text-gray-600 hover:text-gray-900">Case Studies</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900">Pricing</a>
              <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                (270) 505-8002
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-20">
          <div className="max-w-3xl">
            <div className="inline-block bg-blue-500 text-sm font-medium px-3 py-1 rounded-full mb-4">
              15,000+ Facebook Followers • 84% Community Recommended
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              Reach Your Ideal Customers in Hardin County
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              As the leading digital news and community platform in Hardin County, we connect your business 
              with thousands of engaged local residents who trust and follow Hardin Local daily. Your message appears 
              exactly where your customers already spend time—engaging with trusted local content.
            </p>
            <div className="flex gap-4 items-center">
              <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-medium hover:bg-blue-50 flex items-center gap-2">
                Get Your Custom Marketing Plan
                <ChevronRight className="w-5 h-5" />
              </button>
              <button className="bg-blue-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Schedule Strategy Call
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Industry Selector */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold mb-6">Select Your Industry to See Relevant Results</h2>
          <div className="grid grid-cols-3 gap-4">
            {Object.keys(industryStats).map((industry) => (
              <button 
                key={industry}
                onClick={() => setSelectedIndustry(industry)}
                className={`p-4 rounded-lg text-left ${
                  selectedIndustry === industry 
                    ? 'bg-blue-50 border-2 border-blue-600' 
                    : 'border-2 border-gray-200'
                }`}
              >
                <h3 className="font-bold mb-2 capitalize">{industry}</h3>
                <p className="text-sm text-gray-600">
                  {industry === 'retail' && 'Stores, Shops, Boutiques'}
                  {industry === 'restaurant' && 'Dining, Cafes, Food Service'}
                  {industry === 'services' && 'Professional & Personal Services'}
                </p>
              </button>
            ))}
          </div>
          
          {/* Industry Stats */}
          <div className="grid grid-cols-3 gap-8 mt-8 pt-8 border-t">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {industryStats[selectedIndustry].engagement}
              </div>
              <div className="text-gray-600">Engagement Rate</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {industryStats[selectedIndustry].reach}
              </div>
              <div className="text-gray-600">Monthly Readers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {industryStats[selectedIndustry].conversion}
              </div>
              <div className="text-gray-600">Average ROI</div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Stories */}
      <div className="max-w-6xl mx-auto px-4 py-16" id="case-studies">
        <h2 className="text-3xl font-bold text-center mb-12">Local Success Stories</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {successStories.map((story, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-blue-600 font-medium mb-4">{story.business}</div>
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500">Challenge:</div>
                  <div>{story.challenge}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Solution:</div>
                  <div>{story.solution}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Result:</div>
                  <div className="font-medium">{story.result}</div>
                </div>
                <div className="text-2xl font-bold text-blue-600">{story.roi}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold text-center mb-12">What Local Business Owners Say</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium">{testimonial.author}</div>
                    <div className="text-sm text-gray-600">{testimonial.business}</div>
                  </div>
                </div>
                <p className="text-gray-600 mb-4">"{testimonial.quote}"</p>
                <div className="text-blue-600 font-medium">{testimonial.metric}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-blue-600 text-white">
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Grow Your Local Business?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Join the leading businesses in Central Kentucky reaching their ideal customers through Hardin Local.
          </p>
          <div className="flex gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-medium hover:bg-blue-50 flex items-center gap-2">
              Get Started Now
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-white font-bold mb-4">Hardin Local</h3>
              <p className="text-sm">
                Your trusted source for local news and community engagement in Central Kentucky.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Coverage Area</h4>
              <ul className="text-sm space-y-2">
                <li>Elizabethtown</li>
                <li>Radcliff</li>
                <li>Vine Grove</li>
                <li>Fort Knox</li>
                <li>Hodgenville</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Resources</h4>
              <ul className="text-sm space-y-2">
                <li>Phone: (270) 505-8002</li>
                <li>Email: phil@taul.me</li>
                <li>Website: community.hardinlocal.com</li>
                <li>Book Free Consultation</li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdvertisingLanding;